"use client";

import { Capacitor } from "@capacitor/core";
import { nativeShareAssetError } from "./share-asset-policy";

export type NativeShareAsset = {
  file: File;
  type: "png" | "mp4" | "webm" | "pdf";
};

export type ShareResult = {
  ok: boolean;
  reason?: string;
};

type SharePayload = {
  dialogTitle?: string;
  asset?: NativeShareAsset;
  title?: string;
  text?: string;
  url?: string;
};

const isNavigatorShareSupported =
  typeof navigator !== "undefined" && typeof navigator.share === "function";

const canNavigatorShareFiles = (file: File) => {
  if (typeof navigator === "undefined") return false;
  if (typeof navigator.canShare !== "function") return false;
  return navigator.canShare({ files: [file] });
};

function toDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read share asset"));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Invalid data URL payload"));
        return;
      }
      resolve(result);
    };
    reader.readAsDataURL(file);
  });
}

async function shareWithNavigator({
  asset,
  title,
  text,
  url,
}: SharePayload): Promise<ShareResult> {
  if (!isNavigatorShareSupported) {
    return { ok: false, reason: "navigator_share_unavailable" };
  }

  try {
    if (asset && !canNavigatorShareFiles(asset.file)) {
      return { ok: false, reason: "navigator_files_unavailable" };
    }
    if (asset) {
      await navigator.share({
        title,
        text,
        url,
        files: [asset.file],
      });
      return { ok: true };
    }

    await navigator.share({ title, text, url });
    return { ok: true };
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      return { ok: false, reason: "aborted" };
    }

    return { ok: false, reason: "navigator_share_failed" };
  }
}

async function shareWithCapacitor({
  dialogTitle,
  asset,
  title,
  text,
  url,
}: SharePayload): Promise<ShareResult> {
  if (!Capacitor.isNativePlatform()) {
    return { ok: false, reason: "native_unavailable" };
  }

  try {
    const { Share } = await import("@capacitor/share");
    if (!Share || typeof Share.share !== "function") {
      return { ok: false, reason: "capacitor_share_missing" };
    }

    if (asset) {
      const { Filesystem, Directory } = await import("@capacitor/filesystem");
      const directory = Directory.Cache;
      const folder = "hiive-share";
      // Receivers may read after the chooser resolves. Prune only expired files
      // on a subsequent share, never immediately on activity selection.
      try {
        const entries = await Filesystem.readdir({ path: folder, directory });
        await Promise.allSettled(
          entries.files
            .filter(
              (entry) =>
                entry.mtime && entry.mtime < Date.now() - 24 * 60 * 60 * 1000,
            )
            .map((entry) =>
              Filesystem.deleteFile({
                path: `${folder}/${entry.name}`,
                directory,
              }),
            ),
        );
      } catch {
        /* A new installation has no share cache yet. */
      }
      const dataUrl = await toDataUrl(asset.file);
      const path = `${folder}/${Date.now()}-${crypto.randomUUID()}-${asset.file.name.replace(/[^a-zA-Z0-9._-]/g, "-")}`;
      const written = await Filesystem.writeFile({
        path,
        directory,
        data: dataUrl.slice(dataUrl.indexOf(",") + 1),
        recursive: true,
      });
      await Share.share({
        title,
        text,
        url,
        files: [written.uri],
        dialogTitle: dialogTitle ?? "Share activity",
      });
      return { ok: true };
    }

    await Share.share({
      title,
      text,
      url,
      dialogTitle: dialogTitle ?? "Share activity",
    });
    return { ok: true };
  } catch (error) {
    if (
      error instanceof Error &&
      /abort|cancel/i.test(`${error.name} ${error.message}`)
    ) {
      return { ok: false, reason: "aborted" };
    }
    return { ok: false, reason: "capacitor_share_failed" };
  }
}

export async function shareViaSystemSheet(
  payload: SharePayload,
): Promise<ShareResult> {
  if (payload.asset && Capacitor.isNativePlatform()) {
    const reason = nativeShareAssetError(payload.asset.file);
    if (reason) return { ok: false, reason };
  }
  const nativeResult = await shareWithCapacitor(payload);
  if (nativeResult.ok || nativeResult.reason === "aborted") {
    return nativeResult;
  }

  return shareWithNavigator(payload);
}

/**
 * Hand a short message to someone, whatever the device can do: the share
 * sheet, else the clipboard, else WhatsApp in a new tab (most of our people
 * would paste it there anyway). Only "failed" leaves the caller with nothing.
 */
export async function shareMessage(payload: {
  title: string;
  text: string;
  url: string;
}): Promise<"shared" | "aborted" | "copied" | "whatsapp" | "failed"> {
  const sheet = await shareViaSystemSheet(payload);
  if (sheet.ok) return "shared";
  if (sheet.reason === "aborted") return "aborted";
  const message = `${payload.text} ${payload.url}`;
  try {
    await navigator.clipboard.writeText(message);
    return "copied";
  } catch {
    // Clipboard is refused without focus or permission; fall through.
  }
  const opened = window.open(
    `https://wa.me/?text=${encodeURIComponent(message)}`,
    "_blank",
    "noopener,noreferrer",
  );
  return opened ? "whatsapp" : "failed";
}
